<template>
  <div class="home">
    <div class="hero">
      <h1>imweb 자동 로그인 관리</h1>
      <p>최대 3개까지 계정을 등록하고 자동 로그인할 수 있습니다</p>
    </div>

    <!-- 계정 등록 폼 -->
    <div class="card">
      <h2>새 계정 등록</h2>
      <div v-if="accounts.length >= 3" class="alert alert-warning">
        최대 3개까지만 등록 가능합니다.
      </div>
      <form v-else @submit.prevent="addAccount" class="account-form">
        <div class="form-group">
          <label>계정 이름 (선택사항)</label>
          <input 
            v-model="newAccount.name" 
            type="text" 
            placeholder="계정 이름" 
            class="input" 
          />
        </div>
        <div class="form-group">
          <label>이메일 *</label>
          <input 
            v-model="newAccount.email" 
            type="email" 
            placeholder="example@imweb.me" 
            class="input" 
            required
          />
        </div>
        <div class="form-group">
          <label>비밀번호 *</label>
          <input 
            v-model="newAccount.password" 
            type="password" 
            placeholder="비밀번호" 
            class="input" 
            required
          />
        </div>
        <button type="submit" :disabled="loading" class="btn-primary">
          {{ loading ? '등록 중...' : '계정 등록' }}
        </button>
      </form>
    </div>

    <!-- 등록된 계정 목록 -->
    <div class="card">
      <h2>등록된 계정 ({{ accounts.length }}/3)</h2>
      <div v-if="accounts.length === 0" class="empty-state">
        등록된 계정이 없습니다.
      </div>
      <div v-else class="accounts-list">
        <div v-for="account in accounts" :key="account.id" class="account-item">
          <div class="account-info">
            <h3>{{ account.name || account.email }}</h3>
            <p class="account-email">{{ account.email }}</p>
            <p v-if="account.createdAt" class="account-date">
              등록일: {{ formatDate(account.createdAt) }}
            </p>
          </div>
          <div class="account-actions">
            <button 
              @click="loginAccount(account.id)" 
              :disabled="loginLoading === account.id" 
              class="btn-login"
            >
              {{ loginLoading === account.id ? '로그인 중...' : '로그인 실행' }}
            </button>
            <button 
              @click="deleteAccount(account.id)" 
              :disabled="loading" 
              class="btn-delete"
            >
              삭제
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 로그인 결과 메시지 -->
    <div v-if="loginResult" class="card" :class="loginResult.success ? 'result-success' : 'result-error'">
      <h3>{{ loginResult.success ? '✅ 로그인 성공' : '❌ 로그인 실패' }}</h3>
      <p>{{ loginResult.message }}</p>
      <button @click="loginResult = null" class="btn-close">닫기</button>
    </div>
  </div>
</template>

<script>
import api from '../utils/api';

export default {
  name: 'HomePage',
  data() {
    return {
      loading: false,
      loginLoading: null,
      accounts: [],
      newAccount: {
        name: '',
        email: '',
        password: ''
      },
      loginResult: null
    };
  },
  async mounted() {
    await this.loadAccounts();
  },
  methods: {
    async loadAccounts() {
      try {
        const response = await api.get('/api/imweb/accounts');
        if (response.data.success) {
          this.accounts = response.data.accounts;
        }
      } catch (error) {
        console.error('계정 로드 실패:', error);
        this.showError('계정 목록을 불러오는데 실패했습니다.');
      }
    },
    async addAccount() {
      if (!this.newAccount.email || !this.newAccount.password) {
        this.showError('이메일과 비밀번호를 입력해주세요.');
        return;
      }

      this.loading = true;
      try {
        console.log('계정 등록 요청 데이터:', {
          email: this.newAccount.email,
          password: this.newAccount.password ? '***' : undefined,
          name: this.newAccount.name
        });
        
        const response = await api.post('/api/imweb/accounts', this.newAccount);
        console.log('계정 등록 응답:', response.data);
        
        if (response.data.success) {
          this.newAccount = { name: '', email: '', password: '' };
          await this.loadAccounts();
          this.showSuccess('계정이 등록되었습니다.');
        }
      } catch (error) {
        console.error('계정 등록 오류:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            data: error.config?.data
          }
        });
        
        const errorMsg = error.response?.data?.error || error.message || '계정 등록에 실패했습니다.';
        this.showError(errorMsg);
      } finally {
        this.loading = false;
      }
    },
    async deleteAccount(accountId) {
      if (!confirm('정말 삭제하시겠습니까?')) return;

      this.loading = true;
      try {
        const response = await api.delete(`/api/imweb/accounts/${accountId}`);
        if (response.data.success) {
          await this.loadAccounts();
          this.showSuccess('계정이 삭제되었습니다.');
        }
      } catch (error) {
        this.showError('계정 삭제에 실패했습니다.');
      } finally {
        this.loading = false;
      }
    },
    async loginAccount(accountId) {
      this.loginLoading = accountId;
      this.loginResult = null;
      
      try {
        const response = await api.post('/api/imweb/login', { accountId });
        if (response.data.success) {
          this.loginResult = {
            success: true,
            message: response.data.message || '로그인이 성공적으로 완료되었습니다.'
          };
        } else {
          this.loginResult = {
            success: false,
            message: response.data.error || '로그인에 실패했습니다.'
          };
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message;
        this.loginResult = {
          success: false,
          message: errorMsg || '로그인 중 오류가 발생했습니다.'
        };
      } finally {
        this.loginLoading = null;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    showSuccess(message) {
      alert(message);
    },
    showError(message) {
      alert(message);
    }
  }
};
</script>

<style scoped>
.home {
  padding: 0;
}

.hero {
  text-align: center;
  margin-bottom: 3rem;
}

.hero h1 {
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--color-text);
  letter-spacing: -0.02em;
}

.hero p {
  font-size: 1.125rem;
  color: var(--color-text-sub);
  font-weight: 400;
}

.card {
  background: var(--color-bg-white);
  border-radius: var(--radius-md);
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border);
  transition: var(--transition);
}

.card:hover {
  box-shadow: var(--shadow);
}

.card h2 {
  margin-bottom: 1.5rem;
  color: var(--color-text);
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.alert {
  padding: 1rem 1.25rem;
  border-radius: var(--radius-md);
  margin-bottom: 1rem;
  border-left: 4px solid;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.alert-warning {
  background-color: #fffbeb;
  color: #92400e;
  border-color: #f59e0b;
}

.account-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: var(--color-text);
  font-size: 0.875rem;
}

.input {
  padding: 0.75rem 1rem;
  border: 1.5px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 1rem;
  width: 100%;
  font-family: 'Pretendard', sans-serif;
  color: var(--color-text);
  background-color: var(--color-bg-white);
  transition: var(--transition);
}

.input:hover {
  border-color: var(--color-border-dark);
}

.input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.btn-primary {
  background-color: var(--color-primary);
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Pretendard', sans-serif;
  transition: var(--transition);
  width: 100%;
  box-shadow: var(--shadow-sm);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
  box-shadow: var(--shadow-sm);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: var(--color-text-sub);
}

.accounts-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.account-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-bg-primary);
  transition: var(--transition);
}

.account-item:hover {
  box-shadow: var(--shadow-sm);
}

.account-info {
  flex: 1;
}

.account-info h3 {
  margin: 0 0 0.5rem 0;
  color: var(--color-text);
  font-size: 1.125rem;
  font-weight: 600;
}

.account-email {
  margin: 0.25rem 0;
  color: var(--color-text-sub);
  font-size: 0.875rem;
}

.account-date {
  margin: 0.25rem 0 0 0;
  color: var(--color-text-light);
  font-size: 0.8125rem;
}

.account-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-login {
  background-color: #10b981;
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Pretendard', sans-serif;
  transition: var(--transition);
  box-shadow: var(--shadow-sm);
}

.btn-login:hover:not(:disabled) {
  background-color: #059669;
  box-shadow: var(--shadow-sm);
}

.btn-login:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-delete {
  background-color: #ef4444;
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Pretendard', sans-serif;
  transition: var(--transition);
  box-shadow: var(--shadow-sm);
}

.btn-delete:hover:not(:disabled) {
  background-color: #dc2626;
  box-shadow: var(--shadow-sm);
}

.btn-delete:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.result-success {
  background-color: #ecfdf5;
  border: 1px solid #10b981;
  border-left-color: #10b981;
}

.result-error {
  background-color: #fef2f2;
  border: 1px solid #ef4444;
  border-left-color: #ef4444;
}

.result-success h3,
.result-error h3 {
  margin-top: 0;
  color: var(--color-text);
}

.result-success p,
.result-error p {
  color: var(--color-text-sub);
  margin: 0.5rem 0;
}

.btn-close {
  margin-top: 1rem;
  background-color: var(--color-text-sub);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Pretendard', sans-serif;
  transition: var(--transition);
}

.btn-close:hover {
  background-color: var(--color-text);
}

@media (max-width: 768px) {
  .account-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .account-actions {
    width: 100%;
  }
  
  .btn-login,
  .btn-delete {
    flex: 1;
  }
}
</style>
